import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { useServerTable } from '@/hooks/use-server-table';
import { ColumnFilterValue } from '@/lib/table-query';
import { useDepartmentScope, useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { createLeaveOverviewTableColumns } from '../leave-overview-table/leave-overview-table-columns';
import { LeaveOverviewDetailDialog } from '../leave-overview-detail-dialog/leave-overview-detail-dialog';
import {
  useGetLeaveOverview,
  useSearchLeaveOverviewEmployees,
} from '../../hooks/use-leave-overview';
import { LeaveOverviewEmployee } from '../../types/leave-overview.types';
import { DEFAULT_LEAVE_OVERVIEW_ORDER } from '../../graphql/leave-overview-queries';

// Matches the debounce this app already uses for type-ahead employee search (EmployeeSearchPicker).
const KEYWORD_SEARCH_DEBOUNCE_MS = 300;

export const LeaveOverviewTab = () => {
  const { t } = useTranslation();
  // The same department scope as the Requests and Sheet tabs (the Department permissions); the
  // picker only narrows within it.
  const {
    departments,
    getDepartmentIdCondition,
    isLoading: isScopeLoading,
  } = useDepartmentScope();
  // Only the department column has a field of its own on the HR term to sort by. The Employee Name and
  // Department column filters are read here (see createLeaveOverviewTableColumns), not sent as a where.
  const { pagination, resetPage, order, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_LEAVE_OVERVIEW_ORDER,
  });
  const filterValueOf = (id: string) =>
    tableProps.columnFilters.find((filter) => filter.id === id)?.value as
      | ColumnFilterValue
      | undefined;
  const nameFilter = filterValueOf('DisplayName');
  const departmentFilter = filterValueOf('DepartmentName');
  const keywordInput = nameFilter?.type === 'text' ? nameFilter.value : '';
  const departmentId =
    departmentFilter?.type === 'options' ? String(departmentFilter.values[0] ?? '') : '';
  const [keyword, setKeyword] = useState('');
  const [detailTarget, setDetailTarget] = useState<LeaveOverviewEmployee | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setKeyword(keywordInput.trim()), KEYWORD_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [keywordInput]);

  const departmentCondition = getDepartmentIdCondition(departmentId);
  const departmentConditionKey = JSON.stringify(departmentCondition ?? '');

  // Resolves the keyword to matching employees; combined with the department condition below by
  // simply adding both as separate keys of the same `where` object (getLeaveOverview ANDs them), same
  // as IsCurrentHrYear/DepartmentId already are. No keyword -> `userIds` stays `undefined` (no filter);
  // a keyword that matches nobody -> an empty array, which getLeaveOverview turns into an empty list
  // instead of silently falling back to the unfiltered one.
  const { data: matchedEmployees, isLoading: isSearchingEmployees } =
    useSearchLeaveOverviewEmployees(keyword);
  const userIds = keyword ? (matchedEmployees ?? []).map((employee) => employee.UserId) : undefined;

  useEffect(() => {
    resetPage();
  }, [departmentConditionKey, keyword, resetPage]);

  const { data, isLoading, error } = useGetLeaveOverview({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    departmentId: departmentCondition,
    userIds,
    order,
  });
  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const { byUserId } = useEmployeesByUserIds(items.map((item) => item.UserId));
  const emailOf = (userId: string) => byUserId.get(userId)?.Email;

  const columns = createLeaveOverviewTableColumns({ t, emailOf, departments });

  return (
    <div className="flex flex-col gap-4">
      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        onRowClick={setDetailTarget}
        isLoading={isLoading || isScopeLoading || (Boolean(keyword) && isSearchingEmployees)}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <LeaveOverviewDetailDialog
        open={Boolean(detailTarget)}
        onOpenChange={(open) => !open && setDetailTarget(null)}
        employee={detailTarget}
      />
    </div>
  );
};
